import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import { currentLanguage } from '../i18n';
import { useTranslation } from 'react-i18next';
import type { SelectOption } from '../components/idsk';

export interface EnumValue {
  code: string;
  label: { sk: string; en: string };
}

/** A code-list value with its parent: the group of an institution type, the region of a district. */
export interface GroupedValue extends EnumValue {
  parent: string;
}

export interface Enums {
  regions: EnumValue[];
  /** Groups of the CVTI SR code list DRUHSK (first character of the code). */
  institutionTypeGroups: EnumValue[];
  /** CVTI SR code list DRUHSK, e.g. 211 Základná škola; parent = an institutionTypeGroups code. */
  institutionTypes: GroupedValue[];
  /** CVTI SR code list ICRKOKO, e.g. 106 Malacky; parent = a regions code. */
  districts: GroupedValue[];
  inspectionStatuses: EnumValue[];
  formTypes: EnumValue[];
  /** Inspection types (reference data), code "1"…"5". */
  inspectionTypes: EnumValue[];
  /** Inspection tasks, e.g. "2.3"; parent = an inspectionTypes code. */
  inspectionTasks: GroupedValue[];
}

export type EnumKind = keyof Enums;

export function useEnums() {
  return useQuery({ queryKey: ['enums'], queryFn: () => api<Enums>('/enums'), staleTime: Infinity });
}

type Lang = 'sk' | 'en';

/** Institution types and inspection tasks are shown with their code, e.g. "211 – Základná škola", "2.3 – Podpora…". */
function text(kind: EnumKind, value: EnumValue, lang: Lang): string {
  return kind === 'institutionTypes' || kind === 'inspectionTasks' ? `${value.code} – ${value.label[lang]}` : value.label[lang];
}

/** Localized label lookup (current UI language) with the code as fallback while loading. */
export function useEnumLabel() {
  const { i18n } = useTranslation();
  const enums = useEnums().data;
  return (kind: EnumKind, code: string | null | undefined): string => {
    if (!code) return '';
    const lang: Lang = i18n.resolvedLanguage === 'en' ? 'en' : 'sk';
    const value = enums?.[kind].find((v) => v.code === code);
    return value ? text(kind, value, lang) : code;
  };
}

/**
 * Select options of an enumeration; institution types are grouped by their group, districts by their region. With
 * `parent`, only the values of that parent are returned (e.g. the districts of one region), without groups.
 */
export function useEnumOptions(kind: EnumKind, parent?: string): SelectOption[] {
  const enums = useEnums().data;
  const lang = currentLanguage();
  useTranslation(); // re-render on language change
  if (!enums) return [];
  const groups = parent
    ? null
    : kind === 'institutionTypes'
      ? enums.institutionTypeGroups
      : kind === 'districts'
        ? enums.regions
        : kind === 'inspectionTasks'
          ? enums.inspectionTypes
          : null;
  return (enums[kind] as (EnumValue & Partial<GroupedValue>)[])
    .filter((v) => !parent || v.parent === parent)
    .map((v) => ({
      value: v.code,
      label: text(kind, v, lang),
      group: groups && v.parent ? groups.find((g) => g.code === v.parent)?.label[lang] : undefined,
    }));
}

/** The parent of a grouped value (the region of a district), or undefined while loading / for an unknown code. */
export function useEnumParent() {
  const enums = useEnums().data;
  return (kind: 'institutionTypes' | 'districts' | 'inspectionTasks', code: string | null | undefined): string | undefined =>
    code ? enums?.[kind].find((v) => v.code === code)?.parent : undefined;
}
